import { useEffect, useMemo, useState } from "react"
import { useParams } from "react-router-dom"

import { useLazyQuery, useMutation } from "@apollo/client"

import styles from "./RereservationCompletePageV2.module.scss"
import RereservationCompleteContactSection from "./contact-section/RereservationCompleteContactSection"
import EmailSection, {
	isLotteryEmailSection,
} from "./email-section/EmailSection"

import dateImage from "@/lib/images/date.svg"
import ClaimingConversionCancelPolicy from "@/public/components/features/rereservation/ClaimingConversionCancelPolicy/ClaimingConversionCancelPolicy"
import { pickClaimingConversionCancelConditionTexts } from "@/public/components/features/rereservation/ClaimingConversionCancelPolicy/ClaimingConversionCancelPolicy.logic"
import { staffDisplayName } from "@/public/components/features/rereservation/freeStaff"
import CompleteIconV2 from "@/public/components/ui/CompleteIcon/CompleteIconV2"
import { isUenoClinicDataSource } from "@/public/domain/dataSource"
import { graphql } from "@/public/gql/__generated__/gql"
import { formatJstDateUntilDay, formatJstHourMinuteOfDate } from "@/utils/date"
import { toStringWithCommas } from "@/utils/strings/number"

const RereservationV2 = graphql(/* GraphQL */ `
	query GetRereservationV2($param: RereservationV2Param!) {
		rereservationV2(param: $param) {
			rereservation {
				cancelId
				customer {
					id
					name
				}
				menuOrCouponNames
				staff {
					scrapingTargetSiteId
					name
				}
				startTime
				price
				durationMinute
				salon {
					id
					iconUrl
					name
					cancelConditionTextsForMenu
					cancelConditionTextsForNoMenu
					contactUrl
				}
				useClaimingConversionRecancelPolicy
			}
		}
	}
`)

const UenoRereservationRequestV1 = graphql(/* GraphQL */ `
	query GetUenoRereservationRequestV1(
		$param: UenoRereservationRequestV1Param!
	) {
		uenoRereservationRequestV1(param: $param) {
			uenoRereservationRequest {
				cancelId
				firstStartTime
				secondStartTime
			}
		}
	}
`)

// 完了画面でも incentive (特に GIFT) を判定したいので、CanceledReservationV2 を
// 追加で叩いて incentive 情報を取得する.
// RereservationV2 type には incentive field がないため、CanceledReservationV2 を流用.
// TODO: schema 側に Rereservation.incentive を追加できればそちらに寄せる.
const CanceledReservationIncentiveForComplete = graphql(/* GraphQL */ `
	query GetCanceledReservationV2ForCompletePage(
		$param: CanceledReservationV2Param!
	) {
		canceledReservationV2(param: $param) {
			customer {
				name
				phoneNumber
			}
			canceledReservation {
				cancelId
				dataSource
				...emailSection
			}
		}
	}
`)

const UpsertRereservedCustomerEmail = graphql(/* GraphQL */ `
	mutation UpsertRereservedCustomerEmail(
		$input: UpsertRereservedCustomerEmailInput!
	) {
		upsertRereservedCustomerEmail(input: $input) {
			__typename
		}
	}
`)

const RereservationCompletePageV2 = () => {
	const cancelID = useParams().cancel_id

	const [getRereservation, getRereservationResult] =
		useLazyQuery(RereservationV2)
	const [getUenoRereservationRequest, getUenoRereservationRequestResult] =
		useLazyQuery(UenoRereservationRequestV1)
	const [getIncentive, getIncentiveResult] = useLazyQuery(
		CanceledReservationIncentiveForComplete
	)

	const [email, setEmail] = useState("")
	const [isEmailSubmitted, setIsEmailSubmitted] = useState(false)

	const [upsertEmailMutation, { loading: isSubmittingEmail }] = useMutation(
		UpsertRereservedCustomerEmail
	)

	const rereservation =
		getRereservationResult.data?.rereservationV2.rereservation

	const uenoRereservationRequest =
		getUenoRereservationRequestResult.data?.uenoRereservationRequestV1
			.uenoRereservationRequest

	const canceledReservationForComplete =
		getIncentiveResult.data?.canceledReservationV2.canceledReservation
	const customerForComplete =
		getIncentiveResult.data?.canceledReservationV2.customer

	const isUenoClinic = isUenoClinicDataSource(
		canceledReservationForComplete?.dataSource
	)

	const salon = rereservation?.salon

	const staff = rereservation?.staff

	const menuOrCouponNames = rereservation?.menuOrCouponNames || []
	const menusAndCouponsLength = menuOrCouponNames.length
	const cancelConditionTexts =
		menusAndCouponsLength > 0
			? salon?.cancelConditionTextsForMenu
			: salon?.cancelConditionTextsForNoMenu
	// 請求転換の再予約は、cancy が前回予約の金額 (0円かどうか) で片方だけ埋めて返すので、値が入っている方を使う
	const claimingConversionCancelConditionTexts =
		pickClaimingConversionCancelConditionTexts(
			salon?.cancelConditionTextsForMenu,
			salon?.cancelConditionTextsForNoMenu
		)

	useEffect(() => {
		if (!cancelID) {
			return
		}
		// incentive (dataSource 判定含む) と再予約情報は独立なので並列で取得する.
		// 上野 (request フロー) のキャンセルでは rereservationV2 は not found エラーになるが、
		// public の errorLink は extensions.code を持たないエラーではアラートを出さないため
		// ユーザーには見えない (下の loading 判定も error で脱出する).
		getIncentive({ variables: { param: { cancelId: cancelID } } })
		getRereservation({ variables: { param: { cancelId: cancelID } } })
	}, [cancelID, getRereservation, getIncentive])

	// 上野クリニックのキャンセルだと判明したら、希望枠の受付内容を追加で取得する
	useEffect(() => {
		if (!cancelID || !isUenoClinic) {
			return
		}
		getUenoRereservationRequest({
			variables: { param: { cancelId: cancelID } },
		})
	}, [cancelID, isUenoClinic, getUenoRereservationRequest])

	const durationMinute = useMemo(() => {
		return (rereservation?.durationMinute ?? 0) + "分"
	}, [rereservation?.durationMinute])

	const menuNames = useMemo(() => {
		if (!menuOrCouponNames || menuOrCouponNames.length === 0) {
			return ["なし"]
		}

		const texts: string[] = []
		for (const name of menuOrCouponNames) {
			texts.push(`・${name}`)
		}
		return texts
	}, [menuOrCouponNames])

	const price = useMemo(() => {
		return "¥" + toStringWithCommas(rereservation?.price ?? 0)
	}, [rereservation?.price])

	const staffName = useMemo(() => {
		if (!staff) return "なし"
		return staffDisplayName(staff.scrapingTargetSiteId, staff.name)
	}, [staff])

	const uenoRequestedStartTimes = useMemo(() => {
		if (!uenoRereservationRequest) {
			return []
		}
		return [
			uenoRereservationRequest.firstStartTime,
			uenoRereservationRequest.secondStartTime,
		].filter((startTime): startTime is string => Boolean(startTime))
	}, [uenoRereservationRequest])

	const handleEmailSubmit = async (e: React.FormEvent) => {
		e.preventDefault()

		if (!cancelID || !email) {
			return
		}

		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
		if (!emailRegex.test(email)) {
			alert("有効なメールアドレスを入力してください")
			return
		}

		try {
			await upsertEmailMutation({
				variables: {
					input: {
						cancelId: cancelID,
						rereservedCustomerEmail: email,
					},
				},
			})
			setIsEmailSubmitted(true)
		} catch {
			alert("メールアドレスの登録に失敗しました。もう一度お試しください。")
		}
	}

	// クエリ実行前・実行中だけ読み込み表示にする。
	// エラー時に無限ローディングにならないよう、loading 以外の待ち条件には
	// 必ず error での脱出を持たせ、取得できた情報だけでページを描画する。
	if (
		!getIncentiveResult.called ||
		!getRereservationResult.called ||
		getIncentiveResult.loading ||
		getRereservationResult.loading ||
		getUenoRereservationRequestResult.loading ||
		(isUenoClinic &&
			!uenoRereservationRequest &&
			!getUenoRereservationRequestResult.error)
	) {
		return <div>読み込み中...</div>
	}

	if (isUenoClinic) {
		return (
			<div className={styles.complete_page}>
				<div className={styles.header}>
					<img src={dateImage} alt="カレンダー" className={styles.headerIcon} />
					<div className={styles.headerText}>ワンクリック再予約フォーム</div>
				</div>
				<div className={styles.icon_container}>
					<CompleteIconV2 />
				</div>
				<h2>受付が完了しました</h2>
				<p className={styles.ueno_contact_note}>
					ご予約はまだ確定しておりません。後日、担当スタッフよりお電話にてご連絡いたします。
				</p>
				<div
					className={`${styles.main_content_container} ${styles.ueno_request_container}`}
				>
					<table>
						<tbody>
							<tr>
								<td className={styles.title}>お名前</td>
								<td className={styles.content}>
									{customerForComplete?.name ?? "未登録"}
								</td>
							</tr>
							<tr>
								<td className={styles.title}>電話番号</td>
								<td className={styles.content}>
									{customerForComplete?.phoneNumber ?? "未登録"}
								</td>
							</tr>
							{uenoRequestedStartTimes.map((startTime, i) => (
								<tr key={startTime}>
									<td className={styles.title}>
										{uenoRequestedStartTimes.length === 1
											? "受付希望日時"
											: `第${i + 1}希望日時`}
									</td>
									<td className={styles.content}>
										{formatJstDateUntilDay(new Date(startTime))}{" "}
										{formatJstHourMinuteOfDate(new Date(startTime))}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
		)
	}

	const emailSection = (
		<EmailSection
			canceledReservation={canceledReservationForComplete ?? null}
			email={email}
			setEmail={setEmail}
			isEmailSubmitted={isEmailSubmitted}
			isSubmittingEmail={isSubmittingEmail}
			handleEmailSubmit={handleEmailSubmit}
		/>
	)
	// LOTTERY (抽選キャンペーン) の時は、応募条件とメール登録フォームを読み落とされないよう
	// 完了メッセージ (チェックマーク) のすぐ下、予約内容より上に出す.
	// それ以外は従来どおり予約内容の下.
	const showsEmailSectionAboveSummary = isLotteryEmailSection(
		canceledReservationForComplete ?? null
	)

	return (
		<div className={styles.complete_page}>
			<div className={styles.header}>
				<img src={dateImage} alt="カレンダー" className={styles.headerIcon} />
				<div className={styles.headerText}>ワンクリック再予約フォーム</div>
			</div>
			<div className={styles.icon_container}>
				<CompleteIconV2 />
			</div>
			<h2>再予約が完了しました</h2>
			{showsEmailSectionAboveSummary && emailSection}
			<div className={styles.main_content_container}>
				<div className={styles.customer_name}>
					{rereservation?.customer.name}様
				</div>
				<table>
					<tbody>
						<tr>
							<td className={styles.title}>ご予約店舗</td>
							<td className={styles.content}>{salon?.name}</td>
						</tr>
						<tr>
							<td className={styles.title}>ご予約日時</td>
							<td className={styles.content}>
								{rereservation?.startTime
									? formatJstDateUntilDay(new Date(rereservation.startTime))
									: ""}
								{rereservation?.startTime &&
									" " +
										formatJstHourMinuteOfDate(
											new Date(rereservation.startTime)
										)}
							</td>
						</tr>
						<tr>
							<td className={styles.title}>施術時間</td>
							<td className={styles.content}>{durationMinute}</td>
						</tr>
						<tr>
							<td className={styles.title}>ご予約メニュー</td>
							<td className={styles.content}>
								{menuNames.map((name, i) => (
									<div key={i}>{name}</div>
								))}
							</td>
						</tr>
						<tr>
							<td className={styles.title}>ご予約金額</td>
							<td className={styles.content}>{price}</td>
						</tr>
						<tr>
							<td className={styles.title}>指名スタッフ</td>
							<td className={styles.content}>{staffName}</td>
						</tr>
					</tbody>
				</table>
			</div>

			<div className={styles.note_wrapper}>
				<p className={styles.note_text}>※ 別途指名料がかかる場合がございます</p>
				<p className={styles.note_text}>
					※ 本フォームからの予約ではリクルートポイントは付与されません
				</p>
			</div>

			{!showsEmailSectionAboveSummary && emailSection}

			{/* 請求転換の再予約は、確認モーダルと同じ警告カードでキャンセルポリシーを出す
			    (通常の文面・キャンセル料テーブルは出さない) */}
			{rereservation?.useClaimingConversionRecancelPolicy &&
				claimingConversionCancelConditionTexts.length > 0 && (
					<div className={styles.claiming_conversion_policy_wrapper}>
						<ClaimingConversionCancelPolicy
							cancelConditionTexts={claimingConversionCancelConditionTexts}
						/>
					</div>
				)}

			{!rereservation?.useClaimingConversionRecancelPolicy &&
				cancelConditionTexts &&
				cancelConditionTexts.length > 0 && (
					<div className={styles.cancel_policy_container}>
						<h3 className={styles.title}>キャンセルポリシー</h3>
						<div className={styles.cancel_policy_container}>
							<p className={styles.content}>
								直前の予約変更・キャンセル、無断キャンセル、遅刻などがあると、
								他のお客様にもご迷惑がかかるだけでなく、時間的・金銭的なロスが発生します。
							</p>
							<p className={styles.content}>
								そのため、料金の値上げを防ぐ目的もあり、以下のキャンセル料を設定しています。
							</p>

							<p className={styles.content}>
								ご理解とご協力を何卒宜しくお願い致します。
							</p>
						</div>
						<div className={styles.cancel_policy_table}>
							<h3>キャンセル料</h3>
							{cancelConditionTexts?.map((text, i) => {
								return (
									<div className={styles.cancel_policy_table_item}>
										{" "}
										<p key={i}>{"・" + text}</p>{" "}
									</div>
								)
							})}
						</div>
					</div>
				)}

			<RereservationCompleteContactSection contactUrl={salon?.contactUrl} />
		</div>
	)
}

export default RereservationCompletePageV2
